/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/ //
import get from 'lodash/get';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleStateManager } from '@msdyn365-commerce-modules/checkout-utilities';
import { Heading } from '@msdyn365-commerce-modules/data-types';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { AsyncResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { publish } from '../../../Utilities/analytics/analytics-dispatcher';
import { getAttributesForCart } from '../../../Utilities/get-product-attributes';
import { getSubscriptionValues, isLineSubscriptionLine } from '../../../Utilities/subscription-manager';

import GuidedCard from '../components/guided-card';

export interface ICheckoutGuidedFormProps {
    items: React.ReactNode[];
    moduleState: IModuleStateManager;
    disableGuidedCheckoutFlow?: boolean;
    isMobile?: boolean;
    isEditor?: boolean;
    resource: {
        checkoutStepTitleFormat: string;
        saveBtnLabel: string;
        modifyBtnLabel: string;
        cancelBtnLabel: string;
        saveAndContinueBtnLabel: string;
    };

    products: AsyncResult<SimpleProduct[]>;
    cart: AsyncResult<ICartState>;
    context: ICoreContext<IGeneric<IAny>>;
}

interface ICheckoutGuidedFormState {
    currentStep: number;
}

/**
 *
 * CheckoutGuidedForm component
 * @extends {React.Component<ICheckoutGuidedFormProps>}
 */
@observer
class CheckoutGuidedForm extends React.Component<ICheckoutGuidedFormProps> {
    public state: ICheckoutGuidedFormState = {
        currentStep: 0
    };

    public componentDidMount(): void {
        /**
         * Append child modules
         */
        const childIds = this.props.items.map((item: React.ReactNode) => get(item, 'props.id'));
        this.props.moduleState.init({ childIds });

        void this._emitAnalyticsEvent(this.state.currentStep);
    }

    public render(): JSX.Element | null {
        const { moduleState, items, resource } = this.props;

        if (!items || !moduleState) {
            return null;
        }

        return (
            <div className='ms-checkout__guided-form'>
                {items.map((item: React.ReactNode) => {
                    const childId = this.getId(item);
                    const step = this.getStep(childId);
                    const title = this.getHeading(item);
                    const state = this.props.moduleState.getModule(childId);
                    const { isReady, isPending, isUpdating, isDisabled, onEdit, onCancel, onSubmit, hasModuleState, hasInitialized } =
                        state || ({} as IModuleStateManager);
                    return (
                        <GuidedCard
                            key={childId}
                            step={step}
                            title={title}
                            resource={resource}
                            disabled={!hasModuleState || isDisabled}
                            isActive={this.isActive(step)}
                            isVisted={this.isVisted(step)}
                            isExpanded={this.isExpanded(step)}
                            isSubmitting={isPending}
                            isMobile={this.props.isMobile}
                            isPending={isPending}
                            isUpdating={isUpdating}
                            hasInitialized={hasInitialized}
                            isReady={isReady}
                            onSubmit={onSubmit}
                            onCancel={onCancel}
                            onEdit={onEdit}
                            onNext={this.onNext}
                            hasControlGroup={!this.props.disableGuidedCheckoutFlow && hasInitialized}
                        >
                            {React.cloneElement(item as React.ReactElement, { enableControl: true })}
                        </GuidedCard>
                    );
                })}
            </div>
        );
    }

    private getEnabledModules = (): string[] => {
        const {
            moduleState: { childIds, getModule }
        } = this.props;
        return childIds.filter((childId: string) => {
            const state = getModule(childId);
            return !!state && !state.isDisabled;
        });
    };

    private getStep = (id: string): number => {
        return this.getEnabledModules().indexOf(id);
    };

    private getId = (item: React.ReactNode): string => {
        return get(item, 'props.id') || '';
    };

    private getHeading = (item: React.ReactNode): Heading => {
        return get(item, 'props.config.heading') || '';
    };

    private isExpanded = (step: number): boolean => {
        if (this.props.isEditor) {
            // Editorial mode: Expand all the drawers
            return true;
        }
        return step > -1 && step <= this.state.currentStep;
    };

    private isActive = (step: number): boolean => {
        return step === this.state.currentStep;
    };

    private isVisted = (step: number): boolean => {
        return step > -1 && step < this.state.currentStep;
    };

    private onNext = (): void => {
        void this._emitAnalyticsEvent(this.state.currentStep + 1);

        this.setState({
            currentStep: this.state.currentStep + 1
        });
    };

    //----------------------------------------------------------
    // Emit a Checkout Step event
    //----------------------------------------------------------
    private async _emitAnalyticsEvent(step: number): Promise<void> {

        // Wait for the cart to load -- we need it before we can request attributes
        const cart = (await this.props.cart).cart;
        if (cart.CartLines && cart.CartLines.length) {

            // Wait for products and attributes to be loaded
            // Everything has been requested as early as possible, so these aren't linear events
            const attributes = await getAttributesForCart(cart.CartLines, this.props.context.actionContext);
            const products = await this.props.products;
            const subFrequency = getSubscriptionValues(cart).frequency;

            // Convert to the format the analytics module requires
            const mappedProducts = cart.CartLines.map(line => {
                const isSubscription = isLineSubscriptionLine(line);

                return {
                    product: products.find(product => product.RecordId === line.ProductId),
                    quantity: line.Quantity,
                    attributes: line.ProductId ? attributes[line.ProductId] : [],
                    subscription: isSubscription,
                    frequency: isSubscription && subFrequency,
                    discounts: line.DiscountLines,
                    context: this.props.context,
                };
            });

            publish('checkout', {
                step: step + 1,     // The cart page is step 0, so start from 1 here
                products: mappedProducts,
                coupon: cart.Coupons?.map(coupon => coupon.Code).join(','),
            });
        }
    }
}

export default CheckoutGuidedForm;
